<script>
  import { onMount } from 'svelte';
  import { apiFetch } from '../lib/auth.js';

  // ── Types ──────────────────────────────────────────────────────────────────
  // attributes shape: { type: 'cigar' | 'whisky' | 'other', vitola?: string, wrapper?: string }
  // headline = name of the item (cigar name, whisky name, etc.)
  // rating = 1–10
  // body = tasting notes / comment

  const REVIEW_TYPES = ['cigar', 'whisky', 'other'];

  const VITOLAS = [
    'Robusto', 'Toro', 'Corona', 'Churchill', 'Belicoso',
    'Perfecto', 'Lancero', 'Gordo', 'Petit Corona', 'Panatela',
    'Figurado', 'Presidente', 'Magnum', 'Short Robusto', 'Other…',
  ];

  const WRAPPERS = [
    'Maduro', 'Natural', 'Colorado Claro', 'Colorado', 'Colorado Maduro',
    'Oscuro', 'Claro', 'Candela', 'Habano', 'Ecuadorian', 'Connecticut',
    'Other…',
  ];

  // ── State ──────────────────────────────────────────────────────────────────
  let reviews     = [];
  let loading     = true;
  let loadError   = '';

  // Filter
  let filterType  = 'all';

  // Form state
  let showForm    = false;
  let editingId   = null;   // null = new, number = edit

  let formName          = '';
  let formVitola        = 'Robusto';
  let formVitolaCustom  = '';
  let formWrapper       = 'Maduro';
  let formWrapperCustom = '';
  let formType          = 'cigar';
  let formRating        = '';
  let formBody          = '';

  let formLoading = false;
  let formError   = '';

  // Delete confirm
  let deletingId  = null;

  // ── Computed ───────────────────────────────────────────────────────────────
  $: filtered = filterType === 'all'
    ? reviews
    : reviews.filter(r => r.attributes?.type === filterType);

  // ── Lifecycle ──────────────────────────────────────────────────────────────
  onMount(loadReviews);

  async function loadReviews() {
    loading   = true;
    loadError = '';
    try {
      reviews = await apiFetch('/custom_reviews');
    } catch (e) {
      loadError = e.message || 'Failed to load';
    } finally {
      loading = false;
    }
  }

  // ── Form helpers ───────────────────────────────────────────────────────────
  function openNew() {
    editingId        = null;
    formName         = '';
    formVitola       = 'Robusto';
    formVitolaCustom = '';
    formWrapper      = 'Maduro';
    formWrapperCustom = '';
    formType         = 'cigar';
    formRating       = '';
    formBody         = '';
    formError        = '';
    showForm         = true;
  }

  function openEdit(r) {
    editingId  = r.id;
    formName   = r.headline || '';
    formType   = r.attributes?.type || 'cigar';
    formRating = r.rating?.toString() || '';
    formBody   = r.body || '';
    formError  = '';

    const savedVitola = r.attributes?.vitola || 'Robusto';
    if (VITOLAS.includes(savedVitola)) {
      formVitola = savedVitola; formVitolaCustom = '';
    } else {
      formVitola = 'Other…'; formVitolaCustom = savedVitola;
    }

    const savedWrapper = r.attributes?.wrapper || 'Maduro';
    if (WRAPPERS.includes(savedWrapper)) {
      formWrapper = savedWrapper; formWrapperCustom = '';
    } else {
      formWrapper = 'Other…'; formWrapperCustom = savedWrapper;
    }

    showForm = true;
  }

  function closeForm() {
    showForm  = false;
    formError = '';
  }

  async function submitForm() {
    formError = '';
    const rating = parseInt(formRating, 10);
    if (!formName.trim())             { formError = 'Name is required';            return; }
    if (isNaN(rating) || rating < 1 || rating > 10) { formError = 'Rating must be 1–10'; return; }

    formLoading = true;
    try {
      const resolvedVitola  = formVitola  === 'Other…' ? formVitolaCustom.trim()  || null : formVitola  || null;
      const resolvedWrapper = formWrapper === 'Other…' ? formWrapperCustom.trim() || null : formWrapper || null;

      const payload = {
        headline:   formName.trim(),
        rating,
        body:       formBody.trim() || null,
        is_public:  false,
        attributes: {
          type:    formType,
          vitola:  resolvedVitola,
          wrapper: resolvedWrapper,
        },
      };

      if (editingId === null) {
        const created = await apiFetch('/custom_reviews', {
          method: 'POST',
          body: JSON.stringify(payload),
        });
        reviews = [created, ...reviews];
      } else {
        const updated = await apiFetch(`/custom_reviews/${editingId}`, {
          method: 'PUT',
          body: JSON.stringify(payload),
        });
        reviews = reviews.map(r => r.id === editingId ? updated : r);
      }

      closeForm();
    } catch (e) {
      formError = e.message || 'Failed to save';
    } finally {
      formLoading = false;
    }
  }

  // ── Delete ─────────────────────────────────────────────────────────────────
  async function confirmDelete(id) {
    deletingId = id;
  }

  async function doDelete() {
    if (!deletingId) return;
    try {
      await apiFetch(`/custom_reviews/${deletingId}`, { method: 'DELETE' });
      reviews    = reviews.filter(r => r.id !== deletingId);
      deletingId = null;
    } catch (e) {
      // surface inline — keep confirm open
      loadError  = e.message || 'Delete failed';
      deletingId = null;
    }
  }

  // ── Display helpers ────────────────────────────────────────────────────────
  function formatDate(str) {
    if (!str) return '';
    return new Date(str).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  }

  function ratingDots(rating) {
    // returns filled/empty array for 1-10 displayed as 10 dots
    return Array.from({ length: 10 }, (_, i) => i < rating);
  }

  function typeLabel(t) {
    return { cigar: 'Cigar', whisky: 'Whisky', other: 'Other' }[t] || t;
  }
</script>

<div class="cr-page">
  <!-- ── Header ── -->
  <div class="cr-header">
    <div class="cr-title-block">
      <span class="cr-title">My Collection</span>
      <span class="cr-count">{filtered.length} entries</span>
    </div>
    <button class="add-btn" on:click={openNew}>
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
        <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
      </svg>
      Add
    </button>
  </div>

  <!-- ── Type filter tabs ── -->
  <div class="filter-tabs">
    {#each ['all', ...REVIEW_TYPES] as t}
      <button
        class="filter-tab"
        class:active={filterType === t}
        on:click={() => filterType = t}
      >
        {t === 'all' ? 'All' : typeLabel(t)}
      </button>
    {/each}
  </div>

  <!-- ── Content ── -->
  {#if loading}
    <div class="state"><span class="spinner"></span> Loading…</div>

  {:else if loadError}
    <div class="state error">{loadError}</div>

  {:else if filtered.length === 0}
    <div class="state empty">
      <svg viewBox="0 0 48 48" fill="none" width="40" height="40">
        <rect x="6" y="19" width="28" height="8" rx="4" stroke="#2a5200" stroke-width="1.5"/>
        <rect x="32" y="21" width="8" height="4" rx="1" fill="#1e3d00" stroke="#3a6b00" stroke-width="1"/>
        <path d="M36 21 Q38 17 36 13" stroke="#2a5200" stroke-width="1" stroke-linecap="round" fill="none" opacity="0.6"/>
      </svg>
      Nothing here yet
    </div>

  {:else}
    <div class="review-list">
      {#each filtered as r (r.id)}
        <div class="review-card">
          <!-- left accent bar by type -->
          <div class="card-accent" class:type-whisky={r.attributes?.type === 'whisky'} class:type-other={r.attributes?.type === 'other'}></div>

          <div class="card-body">
            <div class="card-top">
              <div class="card-name-block">
                <span class="card-name">{r.headline || '—'}</span>
                {#if r.attributes?.vitola || r.attributes?.wrapper}
                  <span class="card-vitola">
                    {[r.attributes?.vitola, r.attributes?.wrapper].filter(Boolean).join(' · ')}
                  </span>
                {/if}
              </div>
              <div class="card-actions">
                <button class="icon-btn" title="Edit" on:click={() => openEdit(r)}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                  </svg>
                </button>
                <button class="icon-btn del" title="Delete" on:click={() => confirmDelete(r.id)}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <polyline points="3 6 5 6 21 6"/>
                    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                    <path d="M10 11v6"/><path d="M14 11v6"/>
                    <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                  </svg>
                </button>
              </div>
            </div>

            <!-- Rating dots -->
            <div class="rating-row">
              <div class="dots">
                {#each ratingDots(r.rating) as filled}
                  <span class="dot" class:filled></span>
                {/each}
              </div>
              <span class="rating-num">{r.rating}/10</span>
            </div>

            {#if r.body}
              <p class="card-notes">{r.body}</p>
            {/if}

            <div class="card-meta">
              <span class="card-type-pill">{typeLabel(r.attributes?.type || 'other')}</span>
              <span class="card-date">{formatDate(r.created_at)}</span>
            </div>
          </div>
        </div>
      {/each}
    </div>
  {/if}
</div>

<!-- ── Add / Edit form modal ── -->
{#if showForm}
  <div class="overlay" on:click|self={closeForm}>
    <div class="modal">
      <div class="modal-header">
        <span class="modal-title">{editingId === null ? 'New Entry' : 'Edit Entry'}</span>
        <button class="close-btn" on:click={closeForm}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
      </div>

      {#if formError}
        <div class="form-error">{formError}</div>
      {/if}

      <!-- Type selector -->
      <div class="field">
        <label>Type</label>
        <div class="type-tabs">
          {#each REVIEW_TYPES as t}
            <button
              class="type-tab"
              class:active={formType === t}
              on:click={() => formType = t}
            >{typeLabel(t)}</button>
          {/each}
        </div>
      </div>

      <div class="field">
        <label for="form-name">Name <span class="hint">cigar / whisky / item name</span></label>
        <input id="form-name" type="text" bind:value={formName} placeholder="e.g. El Rey de la Caida" maxlength="60" />
      </div>

      {#if formType === 'cigar'}
        <div class="field-row">
          <div class="field">
            <label for="form-vitola">Vitola</label>
            <select id="form-vitola" bind:value={formVitola}>
              {#each VITOLAS as v}
                <option value={v}>{v}</option>
              {/each}
            </select>
            {#if formVitola === 'Other…'}
              <input type="text" bind:value={formVitolaCustom} placeholder="Enter vitola…" class="sub-input" />
            {/if}
          </div>
          <div class="field">
            <label for="form-wrapper">Wrapper</label>
            <select id="form-wrapper" bind:value={formWrapper}>
              {#each WRAPPERS as w}
                <option value={w}>{w}</option>
              {/each}
            </select>
            {#if formWrapper === 'Other…'}
              <input type="text" bind:value={formWrapperCustom} placeholder="Enter wrapper…" class="sub-input" />
            {/if}
          </div>
        </div>
      {/if}

      <div class="field">
        <label>Rating</label>
        <div class="rating-pips">
          {#each Array.from({length:10},(_,i)=>i+1) as n}
            <button
              class="pip"
              class:active={parseInt(formRating) === n}
              on:click={() => formRating = n.toString()}
            >{n}</button>
          {/each}
        </div>
      </div>

      <div class="field">
        <label for="form-body">Notes <span class="hint">optional</span></label>
        <textarea id="form-body" bind:value={formBody} placeholder="Tasting notes, impressions…" rows="3" maxlength="5000"></textarea>
      </div>

      <div class="btn-row">
        <button class="btn-secondary" on:click={closeForm} disabled={formLoading}>Cancel</button>
        <button class="btn-primary" on:click={submitForm} disabled={formLoading}>
          {formLoading ? 'Saving…' : (editingId === null ? 'Add Entry' : 'Save Changes')}
        </button>
      </div>
    </div>
  </div>
{/if}

<!-- ── Delete confirm ── -->
{#if deletingId !== null}
  <div class="overlay" on:click|self={() => deletingId = null}>
    <div class="modal modal-sm">
      <div class="modal-header">
        <span class="modal-title">Delete Entry</span>
      </div>
      <p class="confirm-text">This can't be undone. Delete this entry?</p>
      <div class="btn-row">
        <button class="btn-secondary" on:click={() => deletingId = null}>Cancel</button>
        <button class="btn-danger" on:click={doDelete}>Delete</button>
      </div>
    </div>
  </div>
{/if}

<style>
  /* ── Page layout ── */
  .cr-page { display: flex; flex-direction: column; gap: 1rem; }

  .cr-header {
    display: flex; align-items: center; justify-content: space-between;
  }
  .cr-title-block { display: flex; align-items: baseline; gap: 0.6rem; }
  .cr-title {
    font-family: 'Rajdhani', sans-serif; font-weight: 700; font-size: 0.7rem;
    letter-spacing: 0.25em; text-transform: uppercase; color: #6db800;
    border-left: 3px solid #3a6b00; padding-left: 0.75rem;
  }
  .cr-count {
    font-family: 'Rajdhani', sans-serif; font-size: 0.55rem; font-weight: 600;
    color: #2a5200; letter-spacing: 0.15em; text-transform: uppercase;
  }

  .add-btn {
    display: flex; align-items: center; gap: 0.3rem;
    background: #1a3300; border: 1px solid #3a6b00; color: #6db800;
    font-family: 'Rajdhani', sans-serif; font-weight: 700; font-size: 0.6rem;
    letter-spacing: 0.2em; text-transform: uppercase;
    padding: 0.45rem 0.8rem; border-radius: 2px; cursor: pointer;
    transition: background 0.2s, box-shadow 0.2s;
  }
  .add-btn:hover { background: #223d00; box-shadow: 0 0 8px rgba(109,184,0,0.3); }
  .add-btn svg { width: 14px; height: 14px; }

  /* ── Filter tabs ── */
  .filter-tabs {
    display: flex; gap: 0.4rem; flex-wrap: wrap;
  }
  .filter-tab {
    font-family: 'Rajdhani', sans-serif; font-weight: 700; font-size: 0.55rem;
    letter-spacing: 0.2em; text-transform: uppercase;
    background: transparent; border: 1px solid #1e3d00; color: #2a5200;
    padding: 0.3rem 0.65rem; border-radius: 2px; cursor: pointer;
    transition: border-color 0.2s, color 0.2s, background 0.2s;
  }
  .filter-tab:hover { border-color: #3a6b00; color: #4a7c00; }
  .filter-tab.active { background: #1a3300; border-color: #3a6b00; color: #6db800; }

  /* ── State ── */
  .state {
    display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 0.75rem;
    padding: 3rem 1rem;
    font-family: 'Rajdhani', sans-serif; font-size: 0.7rem; font-weight: 600;
    letter-spacing: 0.15em; text-transform: uppercase; color: #2a5200;
  }
  .state.error { color: #cc6633; }
  .state.empty { color: #1e3d00; }
  .spinner {
    width: 16px; height: 16px;
    border: 2px solid #1e3d00; border-top-color: #6db800;
    border-radius: 50%; animation: spin 0.7s linear infinite;
  }
  @keyframes spin { to { transform: rotate(360deg); } }

  /* ── Review list ── */
  .review-list { display: flex; flex-direction: column; gap: 0.6rem; }

  .review-card {
    display: flex; background: #0f0f0f; border: 1px solid #1a1a1a; border-radius: 3px;
    overflow: hidden; transition: border-color 0.2s;
  }
  .review-card:hover { border-color: #1e3d00; }

  .card-accent {
    width: 3px; flex-shrink: 0;
    background: #3a6b00; /* cigar = green */
  }
  .card-accent.type-whisky { background: #7a5200; }
  .card-accent.type-other  { background: #2a2a4a; }

  .card-body {
    flex: 1; padding: 0.75rem; display: flex; flex-direction: column; gap: 0.45rem; min-width: 0;
  }

  .card-top {
    display: flex; align-items: flex-start; justify-content: space-between; gap: 0.5rem;
  }
  .card-name-block { display: flex; flex-direction: column; gap: 0.1rem; min-width: 0; }
  .card-name {
    font-family: 'Rajdhani', sans-serif; font-weight: 700; font-size: 1rem;
    color: #d0d0d0; letter-spacing: 0.03em;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  }
  .card-vitola {
    font-family: 'Rajdhani', sans-serif; font-size: 0.55rem; font-weight: 700;
    letter-spacing: 0.2em; text-transform: uppercase; color: #3a6b00;
  }

  .card-actions { display: flex; gap: 0.3rem; flex-shrink: 0; }
  .icon-btn {
    background: none; border: 1px solid #1e3d00; border-radius: 2px;
    color: #2a5200; cursor: pointer; padding: 0.3rem;
    transition: color 0.2s, border-color 0.2s, background 0.2s;
    display: flex; align-items: center; justify-content: center;
  }
  .icon-btn svg { width: 13px; height: 13px; }
  .icon-btn:hover { color: #6db800; border-color: #3a6b00; background: rgba(74,124,0,0.06); }
  .icon-btn.del:hover { color: #cc4400; border-color: #5a2000; background: rgba(204,68,0,0.06); }

  /* ── Rating dots ── */
  .rating-row { display: flex; align-items: center; gap: 0.5rem; }
  .dots { display: flex; gap: 3px; }
  .dot {
    width: 7px; height: 7px; border-radius: 50%;
    background: #1e2e00; border: 1px solid #2a5200;
    transition: background 0.15s;
  }
  .dot.filled { background: #6db800; border-color: #6db800; box-shadow: 0 0 4px rgba(109,184,0,0.5); }
  .rating-num {
    font-family: 'Rajdhani', sans-serif; font-weight: 700; font-size: 0.6rem;
    color: #3a6b00; letter-spacing: 0.1em;
  }

  /* ── Notes ── */
  .card-notes {
    font-family: 'Rajdhani', sans-serif; font-size: 0.8rem; font-weight: 600;
    color: #666; line-height: 1.45;
  }

  /* ── Card meta ── */
  .card-meta { display: flex; align-items: center; gap: 0.5rem; }
  .card-type-pill {
    font-family: 'Rajdhani', sans-serif; font-size: 0.5rem; font-weight: 700;
    letter-spacing: 0.15em; text-transform: uppercase;
    background: #0d1a00; border: 1px solid #1e3d00; border-radius: 2px;
    padding: 0.1rem 0.35rem; color: #2a5200;
  }
  .card-date {
    font-family: 'Rajdhani', sans-serif; font-size: 0.5rem; font-weight: 600;
    color: #2a2a2a; letter-spacing: 0.1em; text-transform: uppercase;
  }

  /* ── Overlay / Modal ── */
  .overlay {
    position: fixed; inset: 0; background: rgba(0,0,0,0.75);
    display: flex; align-items: flex-end; justify-content: center;
    z-index: 200; padding: 0;
  }
  .modal {
    background: #0d0d0d; border: 1px solid #1e3d00; border-radius: 3px 3px 0 0;
    width: 100%; max-width: 480px; max-height: 92dvh;
    display: flex; flex-direction: column; gap: 1rem;
    padding: 1.25rem; overflow-y: auto;
    animation: slideUp 0.22s ease;
  }
  .modal-sm { max-height: 60dvh; }
  @keyframes slideUp { from { transform: translateY(30px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }

  .modal-header {
    display: flex; align-items: center; justify-content: space-between;
  }
  .modal-title {
    font-family: 'Rajdhani', sans-serif; font-weight: 700; font-size: 0.65rem;
    letter-spacing: 0.3em; text-transform: uppercase; color: #3a6b00;
    border-left: 3px solid #3a6b00; padding-left: 0.6rem;
  }
  .close-btn {
    background: none; border: none; color: #2a5200; cursor: pointer; padding: 0.2rem;
    transition: color 0.2s;
  }
  .close-btn svg { width: 16px; height: 16px; }
  .close-btn:hover { color: #cc4400; }

  /* ── Form fields ── */
  .form-error {
    background: rgba(204,68,0,0.1); border: 1px solid rgba(204,68,0,0.3); border-radius: 2px;
    color: #cc6633; font-family: 'Rajdhani', sans-serif; font-size: 0.65rem;
    letter-spacing: 0.1em; padding: 0.6rem 0.75rem; text-transform: uppercase;
  }

  .field { display: flex; flex-direction: column; gap: 0.4rem; }
  .field label {
    font-family: 'Rajdhani', sans-serif; font-weight: 600;
    font-size: 0.55rem; letter-spacing: 0.25em; text-transform: uppercase; color: #3a6b00;
  }
  .hint { font-weight: 400; color: #2a5200; text-transform: none; letter-spacing: 0.05em; }

  .field input, .field textarea {
    background: #0a0a0a; border: 1px solid #1e3d00; border-radius: 2px;
    color: #c8c8c8; font-family: 'Rajdhani', sans-serif;
    font-size: 1rem; font-weight: 600; padding: 0.6rem 0.75rem;
    width: 100%; outline: none; transition: border-color 0.2s, box-shadow 0.2s;
    box-sizing: border-box;
  }
  .field textarea { resize: vertical; min-height: 72px; }
  .field input:focus, .field textarea:focus { border-color: #3a6b00; box-shadow: 0 0 0 2px rgba(74,124,0,0.15); }

  /* ── Side-by-side field row ── */
  .field-row { display: grid; grid-template-columns: 1fr 1fr; gap: 0.75rem; }

  /* ── Select ── */
  .field select {
    background: #0a0a0a; border: 1px solid #1e3d00; border-radius: 2px;
    color: #c8c8c8; font-family: 'Rajdhani', sans-serif;
    font-size: 1rem; font-weight: 600; padding: 0.6rem 0.75rem;
    width: 100%; outline: none; cursor: pointer;
    transition: border-color 0.2s, box-shadow 0.2s;
    appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath d='M0 0l5 6 5-6z' fill='%232a5200'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 0.65rem center;
    padding-right: 2rem;
  }
  .field select:focus { border-color: #3a6b00; box-shadow: 0 0 0 2px rgba(74,124,0,0.15); }
  .field select option { background: #0d0d0d; color: #c8c8c8; }

  .sub-input {
    margin-top: 0.35rem;
    background: #0a0a0a; border: 1px solid #1e3d00; border-radius: 2px;
    color: #c8c8c8; font-family: 'Rajdhani', sans-serif;
    font-size: 0.9rem; font-weight: 600; padding: 0.5rem 0.65rem;
    width: 100%; outline: none; box-sizing: border-box;
    transition: border-color 0.2s;
  }
  .sub-input:focus { border-color: #3a6b00; }

  /* ── Type tabs (in form) ── */
  .type-tabs { display: flex; gap: 0.4rem; }
  .type-tab {
    font-family: 'Rajdhani', sans-serif; font-weight: 700; font-size: 0.55rem;
    letter-spacing: 0.2em; text-transform: uppercase;
    background: transparent; border: 1px solid #1e3d00; color: #2a5200;
    padding: 0.3rem 0.65rem; border-radius: 2px; cursor: pointer;
    transition: border-color 0.2s, color 0.2s, background 0.2s;
  }
  .type-tab:hover { border-color: #3a6b00; color: #4a7c00; }
  .type-tab.active { background: #1a3300; border-color: #3a6b00; color: #6db800; }

  /* ── Rating pip buttons ── */
  .rating-pips { display: flex; gap: 0.3rem; }
  .pip {
    font-family: 'Rajdhani', sans-serif; font-weight: 700; font-size: 0.6rem;
    width: 26px; height: 26px; border-radius: 2px;
    background: #0d1a00; border: 1px solid #1e3d00; color: #2a5200;
    cursor: pointer; transition: background 0.15s, border-color 0.15s, color 0.15s;
    display: flex; align-items: center; justify-content: center;
  }
  .pip:hover { border-color: #3a6b00; color: #4a7c00; }
  .pip.active { background: #1a3300; border-color: #6db800; color: #6db800; box-shadow: 0 0 6px rgba(109,184,0,0.3); }

  /* ── Buttons ── */
  .btn-row { display: flex; gap: 0.75rem; padding-top: 0.25rem; }
  .btn-primary {
    flex: 1; background: #1a3300; border: 1px solid #3a6b00; color: #6db800;
    font-family: 'Rajdhani', sans-serif; font-weight: 700; font-size: 0.6rem;
    letter-spacing: 0.25em; text-transform: uppercase; padding: 0.7rem;
    cursor: pointer; border-radius: 2px; transition: background 0.2s, box-shadow 0.2s;
  }
  .btn-primary:hover { background: #223d00; box-shadow: 0 0 10px rgba(109,184,0,0.25); }
  .btn-primary:disabled { opacity: 0.4; cursor: not-allowed; }

  .btn-secondary {
    background: transparent; border: 1px solid #1e3d00; color: #444;
    font-family: 'Rajdhani', sans-serif; font-weight: 600; font-size: 0.6rem;
    letter-spacing: 0.2em; text-transform: uppercase; padding: 0.5rem 1rem;
    cursor: pointer; border-radius: 2px; transition: border-color 0.2s, color 0.2s;
  }
  .btn-secondary:hover { border-color: #3a6b00; color: #6db800; }
  .btn-secondary:disabled { opacity: 0.4; cursor: not-allowed; }

  .btn-danger {
    flex: 1; background: rgba(204,68,0,0.1); border: 1px solid #5a2000; color: #cc4400;
    font-family: 'Rajdhani', sans-serif; font-weight: 700; font-size: 0.6rem;
    letter-spacing: 0.25em; text-transform: uppercase; padding: 0.7rem;
    cursor: pointer; border-radius: 2px; transition: background 0.2s, border-color 0.2s;
  }
  .btn-danger:hover { background: rgba(204,68,0,0.18); border-color: #cc4400; }

  /* ── Delete confirm ── */
  .confirm-text {
    font-family: 'Rajdhani', sans-serif; font-size: 0.85rem; font-weight: 600;
    color: #666; line-height: 1.4;
  }
</style>
